import { useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { Breadcrumbs, CtaBand } from "@/components/content/blocks";
import { JsonSections, proseClass } from "@/components/json-blog/Blocks";
import { InlineText } from "@/components/json-blog/InlineText";
import { JsonFaq, JsonRelated, JsonSources } from "@/components/json-blog/ArticleExtras";
import { useLanguage } from "@/lib/i18n";
import type { JsonPost } from "@/lib/blog/schema";
import { jsonPostSeo } from "@/lib/blog/seo";
import { applyPageSeo, appStoreUrlFor, removeJsonLd, upsertJsonLd, upsertMeta } from "@/lib/seo";

export default function JsonBlogArticle({ post }: { post: JsonPost }) {
  const { language } = useLanguage();
  const c = post[language];
  const isFr = language === "fr";
  useEffect(() => {
    const seo = jsonPostSeo(post, language);
    applyPageSeo({ lang: language, title: c.metaTitle, description: c.metaDescription, keywords: c.keywords, path: seo.path, frPath: post.frPath, enPath: post.enPath, ogImage: seo.image, ogType: "article" });
    upsertMeta("property", "og:image:alt", c.coverAlt);
    upsertMeta("property", "og:image:width", String(post.cover.width));
    upsertMeta("property", "og:image:height", String(post.cover.height));
    upsertMeta("property", "article:published_time", post.datePublished);
    upsertMeta("property", "article:modified_time", post.dateModified);
    upsertMeta("property", "article:section", c.category);
    removeJsonLd("faq");
    seo.blocks.forEach((block) => upsertJsonLd(block.id, block.data));
  }, [post, language, c]);
  return <Layout><article className="relative">
    <section className="relative overflow-hidden noise-bg pt-28 pb-10 md:pt-36 md:pb-12">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 70% 50% at 50% 0%, hsl(var(--primary) / 0.10) 0%, transparent 70%)" }} />
      <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
        <Breadcrumbs trail={[{ label: isFr ? "Accueil" : "Home", href: isFr ? "/" : "/en" }, { label: "Blog", href: isFr ? "/blog" : "/en/blog" }, { label: c.breadcrumb }]} />
        <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground mb-4">
          <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary">{c.category}</span>
          <time dateTime={post.datePublished}>{c.date}</time><span aria-hidden="true">·</span><span>{c.readingTime}</span>
        </div>
        <p className="text-xs text-muted-foreground mb-5"><time dateTime={post.lastFactCheck}>{c.factCheckLabel}</time></p>
        <span className="block text-xs font-bold uppercase tracking-[0.18em] text-primary mb-4">{c.eyebrow}</span>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">{c.h1}</h1>
        <div className="max-w-2xl space-y-4">{c.intro.map((text, i) => <p key={i} className={proseClass}><InlineText text={text} /></p>)}</div>
        <img src={post.cover.src} width={post.cover.width} height={post.cover.height} alt={c.coverAlt} loading="eager" decoding="async" className="mt-8 w-full h-auto rounded-2xl border border-border/60" />
      </div>
    </section>
    <div className="mx-auto max-w-3xl px-4 sm:px-6 pb-16 space-y-14 md:space-y-16">
      <JsonSections sections={c.sections} /><JsonFaq copy={c} />
      <CtaBand title={c.ctaTitle} body={c.ctaBody} appStoreUrl={appStoreUrlFor(language)} freeLabel={isFr ? "Gratuit à télécharger. Disponible sur iPhone et Android." : "Free to download. Available on iPhone and Android."} />
      <JsonRelated copy={c} /><JsonSources copy={c} />
    </div>
  </article></Layout>;
}
